import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiCheckCircle, HiExclamationCircle } from 'react-icons/hi';
import { supabase } from '../supabase/client';

const STEPS = ['Stripe', 'Resend', 'Done'];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {STEPS.map((label, i) => (
        <div key={label} className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors
            ${i < current ? 'bg-indigo-600 text-white' : i === current ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-600' : 'bg-gray-100 text-gray-400'}`}>
            {i < current ? '✓' : i + 1}
          </div>
          <span className={`text-sm font-medium ${i === current ? 'text-indigo-700' : 'text-gray-400'}`}>{label}</span>
          {i < STEPS.length - 1 && <div className="w-8 h-px bg-gray-200 mx-1" />}
        </div>
      ))}
    </div>
  );
}

function InputField({ label, hint, error, ...props }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{label}</label>
      <input
        {...props}
        className={`w-full px-3 py-2.5 border rounded-xl text-sm text-gray-900 placeholder-gray-400
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition
          ${error ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`}
      />
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      {hint && !error && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

export default function Setup() {
  const navigate  = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors,  setErrors]  = useState({});

  const [stripeSecretKey,      setStripeSecretKey]      = useState('');
  const [stripePublishableKey, setStripePublishableKey] = useState('');
  const [resendApiKey,         setResendApiKey]         = useState('');
  const [fromEmail,            setFromEmail]            = useState('');

  const validate = async () => {
    setLoading(true);
    setErrors({});

    // Basic format validation
    const errs = {};
    if (!stripeSecretKey.startsWith('sk_'))      errs.stripeSecretKey      = 'Must start with sk_test_ or sk_live_';
    if (!stripePublishableKey.startsWith('pk_')) errs.stripePublishableKey = 'Must start with pk_test_ or pk_live_';
    if (!resendApiKey.startsWith('re_'))         errs.resendApiKey         = 'Must start with re_';

    if (Object.keys(errs).length) {
      setErrors(errs);
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          stripe_secret_key:      stripeSecretKey,
          stripe_publishable_key: stripePublishableKey,
          resend_api_key:         resendApiKey,
          from_email:             fromEmail || 'onboarding@resend.dev',
          setup_complete:         true,
        },
      });

      if (error) {
        setErrors({ general: error.message });
        setLoading(false);
        return;
      }

      await supabase.auth.refreshSession();
      setStep(2);
    } catch (err) {
      setErrors({ general: err.message });
    }
    setLoading(false);
  };

  // Step 0 — Stripe
  if (step === 0) return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">P</span>
            </div>
            <span className="text-xl font-bold text-gray-900">PortalFlow</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Connect your Stripe account</h1>
          <p className="text-sm text-gray-500 mt-2">Invoice payments will go directly to your Stripe account</p>
        </div>

        <StepIndicator current={0} />

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
          <InputField
            label="Stripe Secret Key"
            hint="Found at dashboard.stripe.com → Developers → API Keys"
            placeholder="sk_test_..."
            type="password"
            value={stripeSecretKey}
            onChange={e => setStripeSecretKey(e.target.value)}
            error={errors.stripeSecretKey}
          />
          <InputField
            label="Stripe Publishable Key"
            hint="Starts with pk_test_ or pk_live_"
            placeholder="pk_test_..."
            value={stripePublishableKey}
            onChange={e => setStripePublishableKey(e.target.value)}
            error={errors.stripePublishableKey}
          />

          {errors.general && (
            <p className="text-sm text-red-500 flex items-center gap-1">
              <HiExclamationCircle className="w-4 h-4" /> {errors.general}
            </p>
          )}

          <button
            onClick={() => setStep(1)}
            disabled={!stripeSecretKey || !stripePublishableKey}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold
              py-2.5 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next →
          </button>

          <p className="text-xs text-center text-gray-400">
            Don't have Stripe?{' '}
            <a href="https://stripe.com" target="_blank" rel="noreferrer" className="text-indigo-600 underline">
              Create a free account
            </a>
          </p>
        </div>
      </div>
    </div>
  );

  // Step 1 — Resend
  if (step === 1) return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">P</span>
            </div>
            <span className="text-xl font-bold text-gray-900">PortalFlow</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Connect your email</h1>
          <p className="text-sm text-gray-500 mt-2">Emails to your clients will be sent via your Resend account</p>
        </div>

        <StepIndicator current={1} />

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
          <InputField
            label="Resend API Key"
            hint="Found at resend.com → API Keys"
            placeholder="re_..."
            type="password"
            value={resendApiKey}
            onChange={e => setResendApiKey(e.target.value)}
            error={errors.resendApiKey}
          />
          <InputField
            label="From Email Address"
            hint="Use onboarding@resend.dev for testing, or your verified domain for production"
            placeholder="you@yourdomain.com"
            type="email"
            value={fromEmail}
            onChange={e => setFromEmail(e.target.value)}
          />

          {errors.general && (
            <p className="text-sm text-red-500 flex items-center gap-1">
              <HiExclamationCircle className="w-4 h-4" /> {errors.general}
            </p>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => setStep(0)}
              className="flex-1 border border-gray-200 text-gray-600 font-semibold
                py-2.5 rounded-xl hover:bg-gray-50 transition"
            >
              ← Back
            </button>
            <button
              onClick={validate}
              disabled={loading || !resendApiKey}
              className="flex-2 flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold
                py-2.5 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Validating…' : 'Validate & Save'}
            </button>
          </div>

          <p className="text-xs text-center text-gray-400">
            Don't have Resend?{' '}
            <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-indigo-600 underline">
              Create a free account
            </a>
          </p>
        </div>
      </div>
    </div>
  );

  // Step 2 — Done
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        <StepIndicator current={2} />
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-4">
          <HiCheckCircle className="w-16 h-16 text-green-500 mx-auto" />
          <h2 className="text-2xl font-bold text-gray-900">You're all set!</h2>
          <p className="text-sm text-gray-500">
            Your Stripe and Resend accounts are connected. Invoice payments will go directly
            to your Stripe account.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold
              py-2.5 rounded-xl transition mt-2"
          >
            Go to Dashboard →
          </button>
        </div>
      </div>
    </div>
  );
}
